import { useState } from "react";
import { toast } from "react-hot-toast";
import AppBar from "../../components/AppBar/AppBar";
import DictionarySearchPanel from "../components/DictionarySearchPanel";
import {
  useDictionaryVersions,
  useImportDictionaryMutation,
} from "../hooks/useVocabQueries";

const DETAIL_FIELDS = [
  ["Group (v032_LPG_id)", "group_id"],
  ["Expanded id", "expanded_id"],
  ["Entry index", "entry_index"],
  ["Entry", "entry"],
  ["Lex", "lex"],
  ["NonCODA", "non_coda"],
  ["Phono", "phono"],
  ["POS", "pos"],
  ["Type", "entry_type"],
  ["English gloss", "english_gloss"],
  ["Root", "root"],
  ["Root+", "root_plus"],
  ["Weak root", "weak_root"],
  ["Root annotation status", "root_annotation_status"],
  ["Exists in MSA LPG", "exists_in_msa_lpg"],
  ["Lex cluster key", "lex_cluster_key"],
  ["Lex cluster (1)", "lex_cluster_1"],
  ["Lex cluster (2)", "lex_cluster_2"],
  ["Source id", "source_id"],
  ["Source root", "entry_root_from_source"],
  ["Source lex", "entry_lex_from_source"],
  ["Source POS", "entry_pos_from_source"],
  ["Source gloss", "entry_gloss_from_source"],
  ["Annotation LPG key", "annotation_lpg_key"],
  ["GAHWA LPG key", "gahwa_lpg_key"],
];

function EntryDetails({ entry }) {
  if (!entry) {
    return <p className="text-sm text-gray-500">Select a variant to see its v0.3.2 fields.</p>;
  }
  return (
    <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-xs">
      {DETAIL_FIELDS.filter(([, key]) => entry[key] !== "" && entry[key] != null).map(
        ([label, key]) => (
          <div key={key} className="contents">
            <dt className="text-gray-500">{label}</dt>
            <dd className="col-span-2 break-words text-gray-900 dark:text-gray-100" dir="auto">
              {String(entry[key])}
            </dd>
          </div>
        )
      )}
    </dl>
  );
}

export default function DictionaryAdminPage() {
  const versionsQuery = useDictionaryVersions({ queryConfig: { queryKey: [] } });
  const [file, setFile] = useState(null);
  const [versionLabel, setVersionLabel] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEntry, setSelectedEntry] = useState(null);

  const importMutation = useImportDictionaryMutation({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Dictionary imported");
        setFile(null);
      },
      onError: (error) =>
        toast.error(error?.response?.data?.[0] || error?.response?.data?.detail || "Import failed"),
    },
  });

  return (
    <div className="min-h-screen dark:bg-ramsalab-deep-navy">
      <AppBar />
      <div className="mx-auto max-w-6xl px-4 py-8 space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-6 dark:bg-ramsalab-light-navy dark:border-gray-700">
            <h2 className="text-sm font-semibold dark:text-white">Import dictionary</h2>
            <p className="mt-1 text-xs text-gray-500">
              Upload the v0.3.2 workbook (.xlsx); entries are read from the "v0.3.2 Expanded"
              sheet and grouped by v032_LPG_id. A CSV export of that sheet also works.
            </p>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-3 block w-full text-sm"
            />
            <input
              type="text"
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder="Version label (optional)"
              className="mt-3 w-full rounded border px-3 py-2 text-sm dark:bg-ramsalab-deep-navy dark:text-white"
            />
            <button
              type="button"
              disabled={!file || importMutation.isPending}
              onClick={() =>
                importMutation.mutate({
                  file,
                  version: versionLabel || undefined,
                  activate: true,
                })
              }
              className="mt-4 rounded-md bg-ramsalab-teal-blue px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {importMutation.isPending ? "Importing…" : "Import and activate"}
            </button>
          </div>

          <div className="rounded-xl border bg-white p-6 dark:bg-ramsalab-light-navy dark:border-gray-700">
            <h2 className="text-sm font-semibold dark:text-white">Versions</h2>
            <ul className="mt-3 space-y-2 text-sm dark:text-gray-200">
              {(versionsQuery.data ?? []).map((v) => (
                <li key={v.id}>
                  {v.version} — {v.row_count} rows
                  {v.is_active ? " (active)" : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-xl border bg-white p-6 dark:bg-ramsalab-light-navy dark:border-gray-700">
          <h2 className="text-sm font-semibold dark:text-white">Browse active dictionary</h2>
          <p className="mt-1 text-xs text-gray-500">
            Results are grouped by LPG group; expand a group to see its variants.
          </p>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <DictionarySearchPanel
              className="h-[60vh]"
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSelectEntry={setSelectedEntry}
              selectedEntryIds={selectedEntry ? [selectedEntry.id] : []}
            />
            <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 p-4 dark:border-ramsalab-navy">
              <EntryDetails entry={selectedEntry} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
